import type { SnRouteConsolidationGroupOutDTO, SnRouteConsolidationOutDTO } from '@api/ringil-api.v2.tsx';
import type { CreatedFromGroup } from '@app/pages/customer/supplier-notification/route-consolidation/routeConsolidationResult.ts';

/**
 * Carries a route optimization from the notification list to the create form and back.
 *
 * The backend keeps nothing, so the browser tab is the only place the result lives: the group being
 * created travels to the form under its own key, the whole result stays for the list to show again
 * when the user comes back, and the form records which groups became shipments. Session storage on
 * purpose — another tab has its own selection and its own optimization.
 *
 * Every access is guarded: storage can be unavailable (private mode, blocked site data), and losing
 * the result must never break the list or the form.
 */

const PREFIX = 'routeConsolidation';
const LAST_RESULT_KEY = `${PREFIX}:last`;
const groupKey = (requestId: string, groupId: number) => `${PREFIX}:${requestId}:${groupId}`;
const createdKey = (requestId: string) => `${PREFIX}:${requestId}:created`;
const editsKey = (requestId: string) => `${PREFIX}:${requestId}:edits`;
const hiddenKey = (requestId: string) => `${PREFIX}:${requestId}:hidden`;

/** What the create form needs from one group, and what it sends back on save. */
export interface StoredConsolidationGroup {
  group: SnRouteConsolidationGroupOutDTO;
  consolidationRequestId: string;
  modelVersion?: string;
  /** Notifications the user took out of the group by removing locations; empty when the group is as optimized. */
  removedSupplierNotificationIds?: string[];
}

const read = <T>(key: string): T | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable: the form then says the optimization has to be run again.
  }
};

const remove = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

export const saveConsolidationGroup = (stored: StoredConsolidationGroup) => write(groupKey(stored.consolidationRequestId, stored.group.groupId), stored);

export const loadConsolidationGroup = (requestId: string, groupId: number): StoredConsolidationGroup | null => read(groupKey(requestId, groupId));

export const saveLastConsolidation = (result: SnRouteConsolidationOutDTO) => write(LAST_RESULT_KEY, result);

export const loadLastConsolidation = (): SnRouteConsolidationOutDTO | null => read(LAST_RESULT_KEY);

/** Forgets the shown result together with everything stored for its groups. */
export const clearLastConsolidation = () => {
  const last = loadLastConsolidation();
  if (last?.consolidationRequestId) {
    (last.groups ?? []).forEach(group => remove(groupKey(last.consolidationRequestId, group.groupId)));
    remove(createdKey(last.consolidationRequestId));
    remove(editsKey(last.consolidationRequestId));
    remove(hiddenKey(last.consolidationRequestId));
  }
  remove(LAST_RESULT_KEY);
};

export const loadCreatedGroups = (requestId: string): Record<number, CreatedFromGroup> => (requestId ? read(createdKey(requestId)) ?? {} : {});

export const markGroupCreated = (requestId: string, groupId: number, created: CreatedFromGroup) =>
  write(createdKey(requestId), { ...loadCreatedGroups(requestId), [groupId]: created });

/** Removed stops (by `loadingOrder`) per group id, as the user left them. */
export const loadGroupEdits = (requestId: string): Record<number, number[]> => (requestId ? read(editsKey(requestId)) ?? {} : {});

export const saveGroupEdits = (requestId: string, edits: Record<number, number[]>) => requestId && write(editsKey(requestId), edits);

/** Groups the user took off the list (and the map), by group id. */
export const loadHiddenGroups = (requestId: string): number[] => (requestId ? read(hiddenKey(requestId)) ?? [] : []);

export const saveHiddenGroups = (requestId: string, groupIds: number[]) => requestId && write(hiddenKey(requestId), groupIds);
